import type {
  Reservation,
  RecurringRule,
  Slot,
  User,
  CreateReservationRequest,
  CreateRecurringRequest,
  ApiResponse,
} from '@cp-parkingslots/shared';

// API base URL - uses VITE_API_URL in production, local Functions for dev
const API_BASE = import.meta.env.VITE_API_URL
  || (window.location.hostname === 'localhost' ? 'http://localhost:7071/api' : '/api');

// Token getter function - set by the app when auth is ready
let getAccessToken: (() => Promise<string | null>) | null = null;

export function setTokenGetter(getter: () => Promise<string | null>) {
  getAccessToken = getter;
}

class ApiError extends Error {
  status: number;
  details?: unknown;

  constructor(message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.details = details;
  }
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // Add authorization header if token getter is available
  if (getAccessToken) {
    const token = await getAccessToken();
    if (token) {
      (headers as Record<string, string>)['Authorization'] = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${API_BASE}${endpoint}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData;
    try {
      errorData = await response.json();
    } catch {
      errorData = { message: response.statusText };
    }

    throw new ApiError(
      errorData.message || 'An error occurred',
      response.status,
      errorData.details
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// Reservations API
export const reservationsApi = {
  getReservations: async (
    startDate: string,
    endDate: string,
    slotId?: string
  ): Promise<Reservation[]> => {
    const params = new URLSearchParams({ startDate, endDate });
    if (slotId) params.append('slotId', slotId);
    const response = await fetchApi<ApiResponse<Reservation[]>>(
      `/reservations?${params}`
    );
    return response.data;
  },

  createReservation: async (
    data: CreateReservationRequest
  ): Promise<Reservation> => {
    const response = await fetchApi<ApiResponse<Reservation>>('/reservations', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteReservation: async (id: string): Promise<void> => {
    await fetchApi<void>(`/reservations/${id}`, {
      method: 'DELETE',
    });
  },
};

// Recurring API
export const recurringApi = {
  getRecurringRules: async (): Promise<RecurringRule[]> => {
    const response = await fetchApi<ApiResponse<RecurringRule[]>>('/recurring');
    return response.data;
  },

  createRecurringRule: async (
    data: CreateRecurringRequest
  ): Promise<RecurringRule> => {
    const response = await fetchApi<ApiResponse<RecurringRule>>('/recurring', {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return response.data;
  },

  deleteRecurringRule: async (id: string): Promise<{ reservationsDeleted: number }> => {
    const response = await fetchApi<{ reservationsDeleted: number }>(
      `/recurring/${id}`,
      {
        method: 'DELETE',
      }
    );
    return response;
  },
};

// Slots API
export const slotsApi = {
  getSlots: async (): Promise<Slot[]> => {
    const response = await fetchApi<ApiResponse<Slot[]>>('/slots');
    return response.data;
  },
};

// Users API
export const usersApi = {
  getCurrentUser: async (): Promise<User> => {
    const response = await fetchApi<ApiResponse<User>>('/users/me');
    return response.data;
  },
};

export { ApiError };
